import { Layout } from "../UI/Layout/Layout.tsx";
import { NavBar } from "../UI/Layout/NavBar.tsx";
import { MainContainer } from "../UI/Layout/MainContainer.tsx";
import { Card } from "../UI/Card.tsx";
import { useNavigate, useSearchParams } from "react-router";
import { useProductInfo } from "./useProductInfo.ts";
import { FullPageLoading } from "../UI/FullPageLoading.tsx";
import { useAnalyticsParams } from "./useAnalyticsParams.ts";
import {
  InvoiceMoneyTransferForm,
  InvoiceMoneyTransferFormParams,
} from "./InvoiceMoneyTransferForm.tsx";
import { Footer } from "../UI/Layout/Footer.tsx";
import { useCreateInvoicePaymentIntent } from "./useCreateInvoicePaymentIntent.ts";
import { useMoneyTransferInfoQueryStringRepository } from "./useMoneyTransferInfoQueryStringRepository.ts";
import { DisplayMoneyTransferInfoScreenPath } from "./DisplayMoneyTransferInfoScreen.tsx";
import { useToMoneyTransferWithoutInvoice } from "./useToMoneyTransferWithoutInvoice.ts";
import { Link } from "../UI/Link.tsx";

export const InvoiceMoneyTransferScreenPath = "/money-transfer-with-invoice";
function InvoiceMoneyTransferScreenInternal({
  productId,
  priceId,
}: {
  productId: string;
  priceId: string;
}) {
  const { productInfo, loading } = useProductInfo({
    productId,
    priceId,
  });

  const { analyticsParams } = useAnalyticsParams();
  const { createInvoicePaymentIntent } = useCreateInvoicePaymentIntent();

  const { decorateUrlWithEncodedMoneyTransferInfo } =
    useMoneyTransferInfoQueryStringRepository();

  const { toMoneyTransferWithoutInvoice } = useToMoneyTransferWithoutInvoice();

  const navigate = useNavigate();
  async function onSubmit(data: InvoiceMoneyTransferFormParams) {
    const moneyTransferInfo = await createInvoicePaymentIntent({
      businessName: data.businessName,
      businessType: data.businessType,
      cap: data.address.cap,
      city: data.address.city,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      pec: data.pec,
      priceId,
      productId,
      province: data.address.province,
      sdiCode: data.sdiCode,
      street: data.address.street,
      taxCode: data.taxCode,
      vat: data.vat,
      ...analyticsParams,
    });

    const url = decorateUrlWithEncodedMoneyTransferInfo(
      moneyTransferInfo,
      new URL(DisplayMoneyTransferInfoScreenPath, window.location.origin),
    );

    navigate(`${url.pathname}${url.search}`);
  }

  if (loading) return <FullPageLoading />;
  if (!productInfo) return null; //TODO implementare 404
  return (
    <Layout>
      <NavBar />
      <MainContainer>
        <div className={"w-full max-w-[1024px]"}>
          <Card>
            <div className={"mb-4 flex flex-col space-y-1"}>
              <h3 className={"font-bold text-xl"}>{productInfo.productName}</h3>
              <p>{productInfo.unitAmount / 100}€</p>
              <p>
                Compila il form qui sotto per ricevere le informazioni per
                procedere al bonifico.
              </p>
              <p className={"mt-2"}>
                Non ti serve la fattura? Clicca{" "}
                <Link
                  label={"qui"}
                  onClick={(e) => {
                    e.preventDefault();
                    toMoneyTransferWithoutInvoice({ productId, priceId });
                  }}
                />
              </p>
            </div>
            <InvoiceMoneyTransferForm onSubmit={onSubmit} />
          </Card>
        </div>
      </MainContainer>
      <Footer />
    </Layout>
  );
}

export function InvoiceMoneyTransferScreen() {
  const [searchParams] = useSearchParams();
  const priceId = searchParams.get("priceId");
  const productId = searchParams.get("productId");
  if (!priceId || !productId) return <p>Missing price or product id</p>; //TODO implementare 404;
  return (
    <InvoiceMoneyTransferScreenInternal
      productId={productId}
      priceId={priceId}
    />
  );
}
